/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useEffect, useRef } from "react";
import { externalReferralApi, useAppSelector } from "@litres-core/store";
import { getCookie, setCookie, getSecondsInYear } from "@litres/utils";

import { externalReferralError } from "../errors";

const ADVCAKE_UTM_PARAMS_TO_REMOVE = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "advcake_params",
];
const LITRES_ADVCAKE_COOKIE_NAME = "litres_adv_cookie";
const ADVCAKE_TRACK_ID_UPDATE_EVENT_NAME = "advcake_track_id_updated";

/**
 * Устаревшие названия кук Adv.Cake для обратной совместимости
 */
const ADVCAKE_TRACK_ID_COOKIE = "advcake_track_id";
const ADVCAKE_TRACK_URL_COOKIE = "advcake_track_url";

/**
 * Новые куки заменяющие собой старые, которые блокируются
 * блокировщиками рекламы
 */
const NEW_ADVCAKE_TRACK_ID_COOKIE = "tj4rxr_track_id";
const NEW_ADVCAKE_TRACK_URL_COOKIE = "tj4rxr_track_url";

export const useTrackAdvCakeReferral = () => {
  const [addAdvCakeReferral] =
    externalReferralApi.useAddAdvCakeReferralMutation();
  const userId = useAppSelector(state => state.foundationUser.id);
  const initialAdvcakeTrackId = useRef(
    getCookie(ADVCAKE_TRACK_ID_COOKIE) || getCookie(NEW_ADVCAKE_TRACK_ID_COOKIE)
  );

  const removeUtmParams = () => {
    const currentUrl = new URL(window.location.href);
    const currentQuery = new URLSearchParams(currentUrl.search);

    ADVCAKE_UTM_PARAMS_TO_REMOVE.forEach(param => {
      currentQuery.delete(param);
    });

    const newQuery = currentQuery.toString();

    const newUrl = newQuery
      ? `${window.location.pathname}?${newQuery}`
      : window.location.pathname;

    window.history.replaceState(null, "", newUrl);
  };

  const advcakeTrackIdUpdateListener = (e: CustomEvent) => {
    const currentAdvcakeTrackId = e.detail?.value;
    const queryParams = new URLSearchParams(window.location.search);

    /**
     * Словили изменение advcake_track_id после обработки utm меток ->
     * Сохраняем внутреннюю куку на случай, если сейчас юзер аноним
     * Если пользователь авторизован -> сохраняем текущие значения кук в базу
     * ВАЖНО: это событие диспатчится не только при переходе с utm метками,
     * поэтому смотрим наличии кверей utm_source и/или lfrom/lfrom_processed
     */
    if (
      currentAdvcakeTrackId &&
      currentAdvcakeTrackId !== initialAdvcakeTrackId.current &&
      (queryParams.has("utm_source") ||
        queryParams.has("lfrom") ||
        queryParams.has("lfrom_processed"))
    ) {
      /**
       * Если пользователь аноним, то сохраняем значения куки и userId
       */
      if (userId === 0) {
        setCookie(
          LITRES_ADVCAKE_COOKIE_NAME,
          `${userId}:${currentAdvcakeTrackId}`,
          {
            "max-age": getSecondsInYear(),
          }
        );
      } else {
        /**
         * Если пользователь авторизован, то сохраняем реферала в базу
         * Дополнительно также затираем trackId из внутренней куки,
         * чтобы не дублировать реферала в базе с одним и тем же trackId
         */
        setTimeout(() => {
          const currentAdvcakeTrackUrl =
            getCookie(ADVCAKE_TRACK_URL_COOKIE) ||
            getCookie(NEW_ADVCAKE_TRACK_URL_COOKIE);

          addAdvCakeReferral({
            track_id: currentAdvcakeTrackId,
            track_url: currentAdvcakeTrackUrl as string,
          })
            .unwrap()
            .then(() => {
              /**
               * Чтобы не сохранять измененный trackId на переходы после сохранения реферала в базу,
               * затираем AdvCake specific utm метки
               * NOTE: Если не затирать, то даже с тем же набором utm меток и их значений, получаем новый trackId
               * что избыточно
               */
              removeUtmParams();
              setCookie(LITRES_ADVCAKE_COOKIE_NAME, `${userId}:`, {
                "max-age": getSecondsInYear(),
              });
            })
            .catch(error => {
              externalReferralError(
                "BackendInteractionError",
                "Add advcake referral request error",
                { originalError: error }
              ).emit({
                track_id: currentAdvcakeTrackId,
                track_url: currentAdvcakeTrackUrl,
              });
            });
        }, 1000);
      }
    }
  };

  useEffect(() => {
    /**
     * Сразу же ожидаем события от Adv.Cake с изменением advcake_track_id
     */
    window.addEventListener(
      ADVCAKE_TRACK_ID_UPDATE_EVENT_NAME,
      advcakeTrackIdUpdateListener as EventListener
    );
    const queryParams = new URLSearchParams(window.location.search);
    /**
     * Если уже было событие advcake_track_id_updated или есть кука advcake_track_id
     */
    if (
      // @ts-ignore
      (window.lastAdvCakeTrackId || initialAdvcakeTrackId.current) &&
      (queryParams.has("utm_source") ||
        queryParams.has("lfrom") ||
        queryParams.has("lfrom_processed"))
    ) {
      /**
       * Сразу же берем то значение, которое есть
       */
      if (userId === 0) {
        setCookie(
          LITRES_ADVCAKE_COOKIE_NAME,
          `${userId}:${initialAdvcakeTrackId.current}`,
          {
            "max-age": getSecondsInYear(),
          }
        );
      } else {
        const currentAdvcakeTrackUrl =
          getCookie(ADVCAKE_TRACK_URL_COOKIE) ||
          getCookie(NEW_ADVCAKE_TRACK_URL_COOKIE);

        addAdvCakeReferral({
          track_id: initialAdvcakeTrackId.current as string,
          track_url: currentAdvcakeTrackUrl as string,
        })
          .unwrap()
          .then(() => {
            removeUtmParams();
            setCookie(LITRES_ADVCAKE_COOKIE_NAME, `${userId}:`, {
              "max-age": getSecondsInYear(),
            });
          })
          .catch(error => {
            externalReferralError(
              "BackendInteractionError",
              "Add advcake referral request error",
              { originalError: error }
            ).emit({
              track_id: initialAdvcakeTrackId.current as string,
              track_url: currentAdvcakeTrackUrl,
            });
          });
      }
    } else {
      /**
       * Если перешли без ?utm_source=advcake или ?utm_source=admitad:
       * 1) Смотрим, были ли переходы ранее под анонимом
       * 2) Смотрим, если авторизованный пользователь, то сохраняем реферала в базу
       * 3) Смотрим, изменился ли userId в сравнении с тем, что сохранили на момент
       * сохранения внутренней куки — нужно, чтобы обновить реферала для нового пользователя
       * с тем же trackId
       */

      if (userId !== 0) {
        const litresAdvCakeCookie = getCookie(LITRES_ADVCAKE_COOKIE_NAME);
        const [savedUserId, savedTrackId] = (litresAdvCakeCookie ?? "").split(
          ":"
        );
        const trackUrl =
          getCookie(ADVCAKE_TRACK_URL_COOKIE) ||
          getCookie(NEW_ADVCAKE_TRACK_URL_COOKIE);
        const currentTrackId =
          getCookie(ADVCAKE_TRACK_ID_COOKIE) ||
          getCookie(NEW_ADVCAKE_TRACK_ID_COOKIE);

        /**
         * Если авторизован и есть сохраненные куки от advcake_track_id ранее под анонимом,
         * то сохраняем реферала в базу
         */
        if (savedTrackId && trackUrl) {
          addAdvCakeReferral({
            track_id: savedTrackId,
            track_url: trackUrl,
          })
            .unwrap()
            .then(() => {
              /**
               * Предотовращаем повторную запись реферала на следующих визитах авторизованным пользователем
               */
              setCookie(LITRES_ADVCAKE_COOKIE_NAME, `${userId}:`, {
                "max-age": getSecondsInYear(),
              });
            })
            .catch(error => {
              externalReferralError(
                "BackendInteractionError",
                "Add advcake referral request error",
                { originalError: error }
              ).emit({
                track_id: savedTrackId,
                track_url: trackUrl,
              });
            });
          return;
        }

        /**
         * Проверяем, изменился ли userId
         */
        if (
          savedUserId &&
          trackUrl &&
          currentTrackId &&
          userId.toString() !== savedUserId
        ) {
          addAdvCakeReferral({
            track_id: currentTrackId,
            track_url: trackUrl,
          })
            .unwrap()
            .then(() => {
              setCookie(LITRES_ADVCAKE_COOKIE_NAME, `${userId}:`, {
                "max-age": getSecondsInYear(),
              });
            })
            .catch(error => {
              externalReferralError(
                "BackendInteractionError",
                "Add advcake referral request error",
                { originalError: error }
              ).emit({
                track_id: currentTrackId,
                track_url: trackUrl,
              });
            });
        }
      }
    }

    return () => {
      window.removeEventListener(
        ADVCAKE_TRACK_ID_UPDATE_EVENT_NAME,
        advcakeTrackIdUpdateListener as EventListener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
};
